import { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ShieldCheck } from 'lucide-react';
import { getDealTransactions, confirmCarrierBind } from '@/services/deals.service.js';

// 200 §2: THE BIND SWITCH.
//
// David's ruling (151 §5): it triggers on BOUND, not on paid. This is where the
// carrier's yes is recorded, and until now there was nowhere for it to go.
//
// IT RECORDS. IT DOES NOT SEND. After a successful flip it OFFERS the packet,
// which has its own produce-and-ask contract; the offer is a button the broker
// presses, never an automatic send.
//
// The policy number and the confirmation reference are TBA-able: a carrier
// often binds before issuing either. Leaving them empty is a first-class
// answer, not a skipped field, and their clocks start from the effective date.
export default function CarrierConfirmDialog({ open, onOpenChange, dealId, onConfirmed, onOfferPacket }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leg, setLeg] = useState(null);
  const [effective, setEffective] = useState('');
  // The DAY the carrier bound it (ruling 10.2). Deliberately NOT prefilled:
  // empty is "not stated" and the row stays NULL. A day nobody typed is never
  // sent. Its own stated fact: it does not drive the closing date, which is
  // the later of the signed paperwork and the money (rulings 12.1).
  const [boundOn, setBoundOn] = useState('');
  const [ref, setRef] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getDealTransactions(dealId);
      const binds = (res?.transactions || []).filter((t) => t.type === 'bind' && !t.voided_at);
      const target = binds[0] || null;
      setLeg(target);
      // Prefilled from the leg, and EDITABLE: the carrier is the authority on
      // this date. If theirs differs the server refuses and names the
      // correction door rather than writing a second date beside this one.
      setEffective(String(target?.effective_date || '').slice(0, 10));
      setRef(target?.carrier_confirmation_ref || '');
    } catch (e) {
      setError(e?.message || 'Could not read the ledger');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const out = await confirmCarrierBind(leg.id, {
        effective_date: effective,
        // SENT ONLY WHEN TYPED. An empty field means "not stated": the row
        // stays NULL. Sending today's date from here would make a default
        // look like a statement.
        bound_on: boundOn.trim() || undefined,
        carrier_confirmation_ref: ref.trim() || undefined,
        policy_number: policyNumber.trim() || undefined,
      });
      if (out?.already_confirmed) {
        toast.info('This bind was already confirmed');
        // A value that did not land is said so, on the same surface: the
        // switch is idempotent and leaves the day alone, so a day typed on a
        // second click was discarded, not recorded.
        if (boundOn.trim()) toast.info('The bound day you typed was not recorded: this bind was already confirmed, and stating a day afterward is a correction with its own door (not built yet)');
      } else toast.success('Carrier confirmation recorded');
      // The day is read back from the row, not echoed from the field: a NULL
      // on the row means no bound day was stated. It says nothing about the
      // closing date, which this day does not drive (rulings 12.1).
      if (!out?.already_confirmed && out?.transaction && out.transaction.carrier_bound_on == null) {
        toast.info('No bound day stated; it stays empty until someone states it');
      }
      // The policy-number write is reported separately: the carrier's yes
      // landed either way, and a failed CRM write must not read as a failed
      // flip.
      if (out?.policy_number && out.policy_number.written === false) {
        toast.error(`Confirmed, but the policy number did not write: ${out.policy_number.error}`);
      }
      onConfirmed?.();
      onOpenChange?.(false);
      // OFFERED, NEVER SENT.
      onOfferPacket?.();
    } catch (e) {
      setError(e?.message || 'Could not record the confirmation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={16} strokeWidth={1.75} /> Record the carrier confirmation
          </DialogTitle>
          <DialogDescription>
            What the carrier agreed, and when. Nothing is sent by this.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-xs text-text-muted">Reading the ledger...</p>
        ) : !leg ? (
          <p className="text-xs text-text-muted">
            No live bind transaction on this deal, so there is nothing for the carrier to have confirmed.
          </p>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-text-muted">Effective date the carrier agreed</span>
              <Input value={effective} onChange={(e) => setEffective(e.target.value)} placeholder="YYYY-MM-DD" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-text-muted">
                Day the carrier bound it <span className="text-text-dim">(leave empty if not stated)</span>
              </span>
              <Input value={boundOn} onChange={(e) => setBoundOn(e.target.value)} placeholder="YYYY-MM-DD, today or earlier" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-text-muted">
                Carrier confirmation reference <span className="text-text-dim">(leave empty if not issued yet)</span>
              </span>
              <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="binder or order number" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wide text-text-muted">
                Policy number <span className="text-text-dim">(leave empty if not issued yet)</span>
              </span>
              <Input value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} placeholder="not issued yet" />
            </label>
            <p className="text-[11px] leading-relaxed text-text-dim">
              A reference or policy number the carrier has not issued stays pending and starts a
              reminder from the effective date. It is not a blank to fill in later quietly.
            </p>
          </div>
        )}

        {error ? <p className="text-[11px] text-danger">{error}</p> : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || loading || !leg || !effective}
            className="bg-accent text-white hover:bg-accent/90"
          >
            {saving ? 'Recording...' : 'Record confirmation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
